import { useState, useEffect, useCallback, useMemo } from 'react';
import leaveService, {
  type LeaveBalance,
  type LeaveBalanceAuditEntry,
  type LeaveBalanceSource,
  type LeaveType,
  type XeroImportRow,
} from '../../services/leave';
import xeroPayrollService from '../../services/xeroPayroll';
import type { ApiError } from '../../types/api';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import { AlertTriangle, History, RefreshCw, X } from 'lucide-react';

const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  annual: 'Annual Leave',
  personal: 'Personal/Sick Leave',
  unpaid: 'Unpaid Leave',
  other: 'Other Leave',
  time_in_lieu: 'Time in Lieu',
  long_service: 'Long Service Leave',
};

// sick and unpaid are excluded from HR editing:
//   sick = same balance as personal (use personal row)
//   unpaid = no tracked balance to adjust
const ALL_LEAVE_TYPES: LeaveType[] = ['annual', 'personal', 'long_service', 'other', 'time_in_lieu'];

function formatHours(value: string | number | undefined | null): string {
  if (value == null) return '0.00';
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
}

interface LeaveBalanceEditorProps {
  /** Target employee. */
  userId: number;
  /** Display name shown in headings and the edit modal. */
  userDisplayName: string;
}

/**
 * Per-employee leave balance editor + permanent audit history: the Leave
 * entitlements tab of the Employee Profile page (`/hr/staff?tab=leave`).
 */
export default function LeaveBalanceEditor({ userId, userDisplayName }: LeaveBalanceEditorProps) {
  const orgTz = useOrgTimezone();
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  // Types Xero owns for this employee: shown read-only, refreshed from Xero.
  const [source, setSource] = useState<LeaveBalanceSource | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [balancesError, setBalancesError] = useState<string | null>(null);

  const [auditEntries, setAuditEntries] = useState<LeaveBalanceAuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditHasMore, setAuditHasMore] = useState(false);

  const [editingType, setEditingType] = useState<LeaveType | null>(null);
  const [editTotal, setEditTotal] = useState<string>('');
  const [editUsed, setEditUsed] = useState<string>('');
  const [editReason, setEditReason] = useState<string>('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);

  const loadBalances = useCallback(async (uid: number) => {
    try {
      setBalancesLoading(true);
      setBalancesError(null);
      const response = await leaveService.adminGetBalances(uid);
      if (response.success && response.data) {
        setBalances(response.data.balances || []);
        setSource(response.data.source ?? null);
      } else {
        setBalances([]);
        setBalancesError(response.message || 'Failed to load balances');
      }
    } catch (err) {
      setBalances([]);
      setBalancesError((err as Error).message || 'Failed to load balances');
    } finally {
      setBalancesLoading(false);
    }
  }, []);

  const loadAudit = useCallback(async (uid: number, offset: number, append: boolean) => {
    try {
      setAuditLoading(true);
      const limit = 50;
      const response = await leaveService.adminGetBalanceAudit(uid, { limit, offset });
      if (response.success && Array.isArray(response.data)) {
        setAuditEntries(prev => append ? [...prev, ...response.data!] : response.data!);
        setAuditHasMore(response.data.length === limit);
        setAuditOffset(offset + response.data.length);
      } else if (!append) {
        setAuditEntries([]);
        setAuditHasMore(false);
      }
    } catch (err) {
      console.error('Failed to load audit log:', err);
      if (!append) setAuditEntries([]);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBalances(userId);
    loadAudit(userId, 0, false);
  }, [userId, loadBalances, loadAudit]);

  // ----- Edit modal helpers -----
  const balanceForType = (type: LeaveType): LeaveBalance | null =>
    balances.find(b => b.leave_type === type) || null;

  const openEdit = (type: LeaveType) => {
    const existing = balanceForType(type);
    setEditingType(type);
    setEditTotal(existing ? formatHours(existing.total_hours) : '0.00');
    setEditUsed(existing ? formatHours(existing.used_hours) : '0.00');
    setEditReason('');
    setEditError(null);
    setEditSuccess(null);
    setEditConfirmOpen(false);
  };

  const closeEdit = () => {
    setEditingType(null);
    setEditConfirmOpen(false);
  };

  const editChanged = useMemo(() => {
    if (!editingType) return false;
    const existing = balanceForType(editingType);
    const oldTotal = existing ? parseFloat(String(existing.total_hours)) : 0;
    const oldUsed = existing ? parseFloat(String(existing.used_hours)) : 0;
    const newTotal = parseFloat(editTotal);
    const newUsed = parseFloat(editUsed);
    if (Number.isNaN(newTotal) || Number.isNaN(newUsed)) return false;
    return Math.abs(newTotal - oldTotal) > 0.001 || Math.abs(newUsed - oldUsed) > 0.001;
  }, [editingType, editTotal, editUsed, balances]); // eslint-disable-line react-hooks/exhaustive-deps

  const editValid = useMemo(() => {
    if (!editChanged) return false;
    if (editReason.trim().length < 5) return false;
    const t = parseFloat(editTotal);
    const u = parseFloat(editUsed);
    if (Number.isNaN(t) || Number.isNaN(u)) return false;
    if (u < 0) return false;
    if (t >= 0 && u > t) return false;
    return true;
  }, [editChanged, editReason, editTotal, editUsed]);

  const handleSubmitEdit = async () => {
    if (!editingType || !editValid) return;
    const existing = balanceForType(editingType);
    const oldTotal = existing ? parseFloat(String(existing.total_hours)) : 0;
    const oldUsed = existing ? parseFloat(String(existing.used_hours)) : 0;
    const newTotal = parseFloat(editTotal);
    const newUsed = parseFloat(editUsed);

    const payload: { total_hours?: number; used_hours?: number; reason: string } = {
      reason: editReason.trim(),
    };
    if (Math.abs(newTotal - oldTotal) > 0.001) payload.total_hours = newTotal;
    if (Math.abs(newUsed - oldUsed) > 0.001) payload.used_hours = newUsed;

    try {
      setEditSaving(true);
      setEditError(null);
      const response = await leaveService.adminEditBalance(userId, editingType, payload);
      if (response.success && response.data) {
        setEditSuccess('Entitlement updated and audit recorded');
        await Promise.all([
          loadBalances(userId),
          loadAudit(userId, 0, false),
        ]);
        setTimeout(() => {
          closeEdit();
          setEditSuccess(null);
        }, 900);
      } else {
        setEditError(response.message || 'Failed to update balance');
      }
    } catch (err: any) {
      setEditError(err?.response?.data?.error?.message || err?.message || 'Failed to update balance');
    } finally {
      setEditSaving(false);
    }
  };

  const ownedByXero = (type: LeaveType) => source?.source === 'xero' && source.owned_types.includes(type);
  const anyOwned = source?.source === 'xero' && source.owned_types.length > 0;

  // Local-mode org: copy this employee's balances from Xero after a preview.
  const xeroImport = source?.source === 'local' ? source.xero_import ?? null : null;
  const [importRows, setImportRows] = useState<XeroImportRow[] | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importSaving, setImportSaving] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importNotice, setImportNotice] = useState<string | null>(null);

  const errorText = (err: unknown, fallback: string) => (err as Error)?.message || fallback;

  const openImport = async () => {
    setImportLoading(true);
    setImportError(null);
    setImportNotice(null);
    setBalancesError(null);
    try {
      const res = await leaveService.adminPreviewXeroImport(userId);
      if (res.success && res.data) setImportRows(res.data.rows);
      else setBalancesError(res.message || 'Could not read balances from Xero');
    } catch (err) {
      setBalancesError(errorText(err, 'Could not read balances from Xero'));
    } finally {
      setImportLoading(false);
    }
  };

  const closeImport = () => {
    setImportRows(null);
    setImportError(null);
  };

  const applyImport = async () => {
    if (!importRows) return;
    setImportSaving(true);
    setImportError(null);
    try {
      const res = await leaveService.adminApplyXeroImport(userId, importRows);
      if (res.success && res.data) {
        const { applied, failed } = res.data;
        await Promise.all([loadBalances(userId), loadAudit(userId, 0, false)]);
        setImportRows(null);
        if (failed.length) {
          setBalancesError(failed.map(f => `${LEAVE_TYPE_LABELS[f.leave_type] ?? f.leave_type}: ${f.error}`).join(' '));
        } else {
          setImportNotice(applied.length
            ? `Updated ${applied.length} balance${applied.length === 1 ? '' : 's'} from Xero.`
            : 'Already matches Xero. Nothing changed.');
        }
      } else {
        setImportError(res.message || 'Could not update balances');
      }
    } catch (err) {
      // Xero moved since the preview: show the new figures and let HR look again.
      const apiErr = err as ApiError;
      if (apiErr.code === 'XERO_BALANCES_CHANGED' && Array.isArray(apiErr.details?.rows)) {
        setImportRows(apiErr.details.rows as XeroImportRow[]);
      }
      setImportError(errorText(err, 'Could not update balances'));
    } finally {
      setImportSaving(false);
    }
  };

  const refreshFromXero = async () => {
    setSyncing(true);
    setBalancesError(null);
    try {
      const res = await xeroPayrollService.syncLeaveBalances(userId);
      // Reload first: it clears the error line, which would wipe the message below.
      await loadBalances(userId);
      if (res.success && res.data?.failed.length) {
        setBalancesError(`Xero: ${res.data.failed[0].error}`);
      } else if (res.success && res.data && res.data.synced === 0) {
        // The server skips, without an error, anyone it has nothing to read for.
        setBalancesError(
          'Nothing was read from Xero for this employee: they are not matched to a current Xero employee, ' +
          'or Xero lists them as terminated. Check Accounts → Xero → Payroll.'
        );
      }
    } catch (err) {
      setBalancesError((err as Error).message || 'Could not read balances from Xero');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Balances table */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-neutral-900">Current Balances</h3>
            <p className="text-sm text-neutral-500 [overflow-wrap:anywhere]">{userDisplayName}</p>
          </div>
          {xeroImport && (
            <button
              type="button"
              onClick={() => void openImport()}
              disabled={!xeroImport.available || importLoading}
              title={xeroImport.reason ?? undefined}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${importLoading ? 'animate-spin' : ''}`} />
              {importLoading ? 'Reading Xero…' : 'Refresh from Xero'}
            </button>
          )}
          {source?.source === 'xero' && (
            <button
              type="button"
              onClick={() => void refreshFromXero()}
              disabled={syncing}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Reading…' : 'Refresh from Xero'}
            </button>
          )}
        </div>

        {anyOwned && (
          <div className="px-6 py-3 text-sm text-neutral-600 bg-blue-50 border-b border-blue-100">
            Balances marked <strong>Xero</strong> are read from Xero as at the last posted pay run
            {source?.synced_at ? ` (read ${formatDateTimeInTz(source.synced_at, orgTz)})` : ''}. <em>Used</em> is leave booked
            but not yet paid. Change these in Xero.
          </div>
        )}
        {source?.source === 'xero' && !source.linked && (
          <div className="px-6 py-3 text-sm text-amber-800 bg-amber-50 border-b border-amber-100">
            Not linked to a Xero employee, so these balances are still calculated in FactreeTech. Match them on
            Accounts → Xero → Payroll.
          </div>
        )}
        {source?.source === 'xero' && source.linked && !anyOwned && (
          <div className="px-6 py-3 text-sm text-amber-800 bg-amber-50 border-b border-amber-100">
            Matched to a Xero employee, but no leave types are matched to Xero, so these balances are still
            calculated in FactreeTech. Match leave types on Accounts → Xero → Payroll.
          </div>
        )}

        {xeroImport && !xeroImport.available && xeroImport.reason && (
          <div className="px-6 py-3 text-sm text-amber-800 bg-amber-50 border-b border-amber-100">
            Can't refresh from Xero: {xeroImport.reason}
          </div>
        )}
        {importNotice && (
          <div className="px-6 py-3 text-sm text-green-700 bg-green-50 border-b border-green-100">{importNotice}</div>
        )}
        {balancesError && (
          <div className="px-6 py-3 text-sm text-red-700 bg-red-50 border-b border-red-100">{balancesError}</div>
        )}
        {balancesLoading ? (
          <div className="px-6 py-8 text-sm text-neutral-500">Loading balances…</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Leave Type</th>
                <th className="text-right px-6 py-3 font-medium">Total Hours</th>
                <th className="text-right px-6 py-3 font-medium">Used Hours</th>
                <th className="text-right px-6 py-3 font-medium">Available</th>
                <th className="text-right px-6 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {ALL_LEAVE_TYPES.map(type => {
                const b = balanceForType(type);
                const total = b ? parseFloat(String(b.total_hours)) : 0;
                const used = b ? parseFloat(String(b.used_hours)) : 0;
                return (
                  <tr key={type}>
                    <td className="px-6 py-3 font-medium text-neutral-900">{LEAVE_TYPE_LABELS[type]}</td>
                    <td className="px-6 py-3 text-right tabular-nums">{total.toFixed(2)}h</td>
                    <td className="px-6 py-3 text-right tabular-nums">{used.toFixed(2)}h</td>
                    <td className="px-6 py-3 text-right tabular-nums font-semibold">
                      {(total - used).toFixed(2)}h
                    </td>
                    <td className="px-6 py-3 text-right">
                      {ownedByXero(type) ? (
                        <span
                          className="inline-flex px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium"
                          title="Read from Xero — change it in Xero"
                        >
                          Xero
                        </span>
                      ) : (
                        <button
                          onClick={() => openEdit(type)}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Audit history */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center gap-2">
          <History className="h-5 w-5 text-neutral-500" />
          <h3 className="text-lg font-semibold text-neutral-900">Audit History</h3>
          <span className="text-xs text-neutral-500 ml-2">Permanent, append-only record</span>
        </div>
        {auditLoading && auditEntries.length === 0 ? (
          <div className="px-6 py-8 text-sm text-neutral-500">Loading history…</div>
        ) : auditEntries.length === 0 ? (
          <div className="px-6 py-8 text-sm text-neutral-500">No changes recorded for this employee.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600">
                <tr>
                  <th className="text-left px-6 py-3 font-medium">When</th>
                  <th className="text-left px-6 py-3 font-medium">Leave Type</th>
                  <th className="text-left px-6 py-3 font-medium">Field</th>
                  <th className="text-right px-6 py-3 font-medium">Old → New</th>
                  <th className="text-left px-6 py-3 font-medium">Reason</th>
                  <th className="text-left px-6 py-3 font-medium">Changed By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {auditEntries.map(entry => (
                  <tr key={entry.id}>
                    <td className="px-6 py-3 text-neutral-700 whitespace-nowrap">
                      {new Date(entry.created_at).toUTCString()}
                    </td>
                    <td className="px-6 py-3 text-neutral-700">{LEAVE_TYPE_LABELS[entry.leave_type] || entry.leave_type}</td>
                    <td className="px-6 py-3 text-neutral-700">{entry.field_changed}</td>
                    <td className="px-6 py-3 text-right tabular-nums">
                      {formatHours(entry.old_value)}h → <strong>{formatHours(entry.new_value)}h</strong>
                    </td>
                    <td className="px-6 py-3 text-neutral-700 max-w-md">{entry.reason}</td>
                    <td className="px-6 py-3 text-neutral-700">
                      {[entry.changed_by_first_name, entry.changed_by_last_name].filter(Boolean).join(' ') || entry.changed_by_username}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {auditHasMore && (
              <div className="px-6 py-4 border-t border-neutral-100 text-center">
                <button
                  disabled={auditLoading}
                  onClick={() => loadAudit(userId, auditOffset, true)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
                >
                  {auditLoading ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Refresh from Xero: review, then copy in */}
      {importRows && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-neutral-900">Refresh from Xero</h3>
              <button onClick={closeImport} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-neutral-600">
                <strong>{userDisplayName}</strong>'s balances as Xero has them, as at the last posted pay run.
                <em> Used</em> is leave booked in Xero and not yet paid. Leave approved here since that pay run and
                not yet in Xero is not counted.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-neutral-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Leave type</th>
                      <th className="text-right px-3 py-2 font-medium">Here (total / used)</th>
                      <th className="text-right px-3 py-2 font-medium">Xero (total / used)</th>
                      <th className="text-right px-3 py-2 font-medium">Available</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {importRows.map(r => {
                      const before = r.current_total_hours - r.current_used_hours;
                      const after = r.xero_total_hours - r.xero_used_hours;
                      return (
                        <tr key={r.leave_type} className={r.changed ? '' : 'text-neutral-400'}>
                          <td className="px-3 py-2 font-medium">{LEAVE_TYPE_LABELS[r.leave_type] ?? r.leave_type}</td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                            {r.current_total_hours.toFixed(2)} / {r.current_used_hours.toFixed(2)}h
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                            {r.xero_total_hours.toFixed(2)} / {r.xero_used_hours.toFixed(2)}h
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                            {r.changed ? <>{before.toFixed(2)} → <strong>{after.toFixed(2)}h</strong></> : 'No change'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-neutral-500">
                Each change is recorded in the audit history. Balances keep being calculated here afterwards.
                Leave types not matched to Xero are left alone.
              </p>
              {importError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{importError}</div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-neutral-200 flex flex-wrap items-center justify-end gap-2">
              <button
                onClick={closeImport}
                disabled={importSaving}
                className="px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => void applyImport()}
                disabled={importSaving || !importRows.some(r => r.changed)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:bg-neutral-300 disabled:cursor-not-allowed"
              >
                {importSaving ? 'Updating…' : "Use Xero's balances"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editingType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-neutral-900">
                Edit {LEAVE_TYPE_LABELS[editingType]}
              </h3>
              <button onClick={closeEdit} className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-neutral-600">
                Editing balance for <strong>{userDisplayName}</strong>.
              </p>

              {editingType === 'time_in_lieu' && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Time in Lieu is automatically accrued from approved timesheets. Manual edits are corrections only —
                    future timesheet approvals will continue to update this balance.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Total hours</label>
                  <input
                    type="number"
                    step="0.25"
                    value={editTotal}
                    onChange={e => setEditTotal(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Used hours</label>
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    value={editUsed}
                    onChange={e => setEditUsed(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  value={editReason}
                  onChange={e => setEditReason(e.target.value)}
                  placeholder="Why is this entitlement being changed? (min 5 characters)"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  This reason will be permanently recorded in the audit log and sent to HR + admins.
                </p>
              </div>

              {editError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{editError}</div>
              )}
              {editSuccess && (
                <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">{editSuccess}</div>
              )}

              {editConfirmOpen && editValid && (
                <div className="border-2 border-primary-300 rounded-lg p-4 bg-primary-50 space-y-2">
                  <p className="text-sm font-semibold text-primary-900">Confirm change</p>
                  {(() => {
                    const existing = balanceForType(editingType);
                    const oldTotal = existing ? parseFloat(String(existing.total_hours)) : 0;
                    const oldUsed = existing ? parseFloat(String(existing.used_hours)) : 0;
                    const newTotal = parseFloat(editTotal);
                    const newUsed = parseFloat(editUsed);
                    return (
                      <ul className="text-sm text-neutral-800 space-y-1">
                        {Math.abs(newTotal - oldTotal) > 0.001 && (
                          <li>
                            Total: <span className="tabular-nums">{oldTotal.toFixed(2)}h → <strong>{newTotal.toFixed(2)}h</strong></span>{' '}
                            ({newTotal >= oldTotal ? '+' : ''}{(newTotal - oldTotal).toFixed(2)}h)
                          </li>
                        )}
                        {Math.abs(newUsed - oldUsed) > 0.001 && (
                          <li>
                            Used: <span className="tabular-nums">{oldUsed.toFixed(2)}h → <strong>{newUsed.toFixed(2)}h</strong></span>{' '}
                            ({newUsed >= oldUsed ? '+' : ''}{(newUsed - oldUsed).toFixed(2)}h)
                          </li>
                        )}
                        <li>
                          New available: <strong className="tabular-nums">{(newTotal - newUsed).toFixed(2)}h</strong>
                        </li>
                      </ul>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-end gap-2">
              <button
                onClick={closeEdit}
                disabled={editSaving}
                className="px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              {!editConfirmOpen ? (
                <button
                  onClick={() => setEditConfirmOpen(true)}
                  disabled={!editValid}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:bg-neutral-300 disabled:cursor-not-allowed"
                >
                  Review change
                </button>
              ) : (
                <button
                  onClick={handleSubmitEdit}
                  disabled={editSaving || !editValid}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:bg-neutral-300 disabled:cursor-not-allowed"
                >
                  {editSaving ? 'Saving…' : 'Confirm & save'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
